import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import { ref, get } from "firebase/database";
import {
  BookOpen, Laptop, ArrowLeftRight, HelpCircle, Gift,
  ShieldCheck, Users, Star, ChevronDown, ChevronUp,
  Bike, FileText, Car, Calendar, GraduationCap, Bell, TrendingUp,
  MessageCircle, Mail, Download
} from "lucide-react";

const FEATURES = [
  { icon: ArrowLeftRight, color: "bg-blue-100 text-blue-600", title: "Buy & Sell", desc: "Sell your old books, electronics, cycle — buy from fellow students at low prices." },
  { icon: ArrowLeftRight, color: "bg-purple-100 text-purple-600", title: "Swap Items", desc: "Exchange items directly — swap your physics book for chemistry notes." },
  { icon: HelpCircle, color: "bg-orange-100 text-orange-600", title: "Lost & Found", desc: "Lost your ID card or water bottle? Post here and get it back from honest students." },
  { icon: Gift, color: "bg-green-100 text-green-600", title: "Free Giveaways", desc: "Seniors give away items for free when they pass out — grab before someone else does." },
  { icon: Car, color: "bg-teal-100 text-teal-600", title: "Rideshare", desc: "Share rides home during holidays — save money and travel safely with classmates." },
  { icon: FileText, color: "bg-yellow-100 text-yellow-600", title: "Buy/Sell Notes", desc: "Buy toppers' notes or sell your own well-made notes to juniors." },
  { icon: Calendar, color: "bg-pink-100 text-pink-600", title: "Events", desc: "Find college fests, seminars, and student events happening near you." },
  { icon: GraduationCap, color: "bg-indigo-100 text-indigo-600", title: "Tutoring", desc: "Find senior students to tutor you in tough subjects at affordable rates." },
  { icon: Users, color: "bg-amber-100 text-amber-600", title: "Parent Zone", desc: "Parents can buy/sell school books, uniforms, bags and more for their children." },
];

const FAQS = [
  { q: "Why CampusSwap instead of OLX or Facebook?", a: "CampusSwap is only for verified college students. Every user is ID-verified, so you deal with real students from real colleges — not strangers from the internet. Safer, faster, more relevant." },
  { q: "Is it free to use?", a: "100% free. No subscription, no commission, no hidden charges. You contact the seller directly via WhatsApp and deal on your own terms." },
  { q: "How does it help me as a student?", a: "Save money buying second-hand items from seniors. Earn money selling things you no longer need. Find rideshare partners for safe travel. Get free items from generous seniors." },
  { q: "How does it help parents?", a: "Buy school textbooks, uniforms, bags and stationery at 50-80% cheaper from other parents whose children have outgrown them." },
  { q: "Is my data safe?", a: "Yes. We use Firebase (by Google) for secure authentication. Your phone number is only shared when you choose to contact someone." },
  { q: "Who can sign up?", a: "Any college student or parent in India. After signup, submit your college ID for verification by admin before you can post listings." },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-gray-50">
        <span className="text-sm font-semibold text-gray-800">{q}</span>
        {open ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
      </button>
      {open && <div className="px-4 pb-3 bg-gray-50 text-sm text-gray-600 leading-relaxed">{a}</div>}
    </div>
  );
}

function CollapsibleSection({ title, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-8">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between mb-1 text-left group">
        <div>
          <h2 className="font-bold text-gray-800 text-xl group-hover:text-blue-600 transition-colors">{title}</h2>
          {subtitle && !open && <p className="text-gray-400 text-xs mt-0.5">{subtitle}</p>}
        </div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${open ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-500"}`}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>
      {subtitle && open && <p className="text-gray-500 text-sm mb-4">{subtitle}</p>}
      {open && children}
    </div>
  );
}

export default function Landing() {
  const [stats, setStats] = useState({ users: 0, listings: 0, colleges: 0 });
  const [topAmbassadors, setTopAmbassadors] = useState([]);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(
    window.matchMedia("(display-mode: standalone)").matches
  );

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  async function handleInstall() {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  }

  useEffect(() => {
    async function fetchStats() {
      try {
        const [usersSnap, listingsSnap] = await Promise.all([
          get(ref(db, "users")),
          get(ref(db, "listings")),
        ]);
        const userCount = usersSnap.exists() ? Object.keys(usersSnap.val()).length : 0;
        let listingCount = 0;
        let collegeSet = new Set();
        if (listingsSnap.exists()) {
          const all = Object.values(listingsSnap.val());
          listingCount = all.length;
          all.forEach(l => { if (l.college) collegeSet.add(l.college); });
        }
        setStats({ users: userCount, listings: listingCount, colleges: collegeSet.size });

        // Compute top ambassadors by referral count
        if (usersSnap.exists()) {
          const allUsers = Object.entries(usersSnap.val()).map(([id, u]) => ({ id, ...u }));
          // Count how many users each referralCode was used by (via referredBy field)
          const referralCounts = {};
          allUsers.forEach(u => {
            if (u.referredBy) {
              referralCounts[u.referredBy] = (referralCounts[u.referredBy] || 0) + 1;
            }
          });
          // Map referralCode back to user
          const ambassadors = allUsers
            .filter(u => u.referralCode && referralCounts[u.referralCode] > 0)
            .map(u => ({ ...u, referralCount: referralCounts[u.referralCode] || 0 }))
            .sort((a, b) => b.referralCount - a.referralCount)
            .slice(0, 5);
          setTopAmbassadors(ambassadors);
        }
      } catch (e) { /* silent */ }
    }
    fetchStats();
  }, []);

  function fmt(n) {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k+`;
    if (n === 0) return "—";
    return `${n}+`;
  }

  return (
    <div className="min-h-screen bg-white pb-10">

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 px-6 pt-12 pb-16 text-center text-white">
        <div className="text-5xl mb-4">🎓</div>
        <h1 className="text-3xl font-bold leading-tight mb-2">
          Campus<span className="text-yellow-300">Swap</span>
        </h1>
        <p className="text-blue-200 text-sm mb-1">India's Verified Campus Marketplace</p>
        <p className="text-white font-semibold text-lg mt-3 leading-snug">
          Buy, Sell & Swap with<br />Fellow Students — Safely
        </p>
        <p className="text-blue-200 text-sm mt-2 mb-6">
          Only for verified college students & parents
        </p>

        {/* Live Stats */}
        <div className="flex justify-center gap-6 mb-8">
          <div className="text-center">
            <p className="text-2xl font-extrabold text-yellow-300">{fmt(stats.users)}</p>
            <p className="text-blue-200 text-xs">Students</p>
          </div>
          <div className="w-px bg-white/20" />
          <div className="text-center">
            <p className="text-2xl font-extrabold text-yellow-300">{fmt(stats.listings)}</p>
            <p className="text-blue-200 text-xs">Listings</p>
          </div>
          <div className="w-px bg-white/20" />
          <div className="text-center">
            <p className="text-2xl font-extrabold text-yellow-300">{stats.colleges > 0 ? `${stats.colleges}+` : "20+"}</p>
            <p className="text-blue-200 text-xs">Colleges</p>
          </div>
        </div>

        <div className="flex flex-col gap-3 max-w-xs mx-auto">
          <Link to="/signup" className="bg-yellow-400 text-gray-900 font-bold py-3 rounded-2xl text-base hover:bg-yellow-300">
            Get Started — It's Free
          </Link>
          <Link to="/login" className="bg-white/20 text-white font-semibold py-3 rounded-2xl text-base hover:bg-white/30 border border-white/30">
            Already have account? Sign In
          </Link>
          {installPrompt && !installed && (
            <button onClick={handleInstall}
              className="bg-white/10 border border-white/40 text-white font-semibold py-3 rounded-2xl text-base hover:bg-white/20 flex items-center justify-center gap-2">
              <Download size={18} /> Install App
            </button>
          )}
          {installed && (
            <p className="text-green-300 text-sm font-medium text-center">✓ App installed on your device</p>
          )}
        </div>
      </div>

      <div className="px-4 max-w-lg mx-auto">

        {/* Social Proof */}
        <div className="mt-6 flex items-center gap-3 bg-green-50 rounded-2xl p-4 border border-green-100">
          <div className="flex -space-x-2">
            {["🧑‍💻", "👩‍🎓", "👨‍🏫", "👩‍💼"].map((e, i) => (
              <div key={i} className="w-9 h-9 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center text-lg">{e}</div>
            ))}
          </div>
          <div>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => <Star key={i} size={13} className="text-yellow-400 fill-yellow-400" />)}
            </div>
            <p className="text-xs text-gray-600 mt-0.5"><strong>Students love it.</strong> Saved ₹5,000+ on average per semester.</p>
          </div>
        </div>

        {/* Problem */}
        <div className="mt-6 bg-red-50 rounded-2xl p-5 border border-red-100">
          <h2 className="font-bold text-gray-800 text-lg mb-3">😤 Sound familiar?</h2>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>❌ Spent ₹800 on a textbook you'll use for just one semester</li>
            <li>❌ Your cycle/laptop is sitting unused after graduation</li>
            <li>❌ Lost your ID card and don't know where to look</li>
            <li>❌ Travelling home alone during holidays — no one to share cab with</li>
            <li>❌ Bought new school books at full price when last year's are available cheap</li>
          </ul>
        </div>

        {/* Solution */}
        <div className="mt-4 bg-green-50 rounded-2xl p-5 border border-green-100">
          <h2 className="font-bold text-gray-800 text-lg mb-3">✅ CampusSwap solves all this</h2>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>✅ Buy books from seniors at 50-70% less price</li>
            <li>✅ Sell your old items and earn money before you pass out</li>
            <li>✅ Post lost items — get them back from honest students</li>
            <li>✅ Find rideshare partners from your own college</li>
            <li>✅ Parents save thousands on school supplies every year</li>
          </ul>
        </div>

        {/* Trust */}
        <div className="mt-4 flex items-center gap-3 bg-blue-50 rounded-2xl p-4 border border-blue-100">
          <ShieldCheck size={36} className="text-blue-600 flex-shrink-0" />
          <div>
            <p className="font-bold text-gray-800">100% Verified Students Only</p>
            <p className="text-xs text-gray-500 mt-0.5">Every user submits their college ID. Admin verifies before they can post. No fake profiles, no fraud.</p>
          </div>
        </div>

        {/* Features */}
        <CollapsibleSection title="What can you do here?" subtitle="Everything a student or parent needs — in one app">
          <div className="grid grid-cols-1 gap-3">
            {FEATURES.map(({ icon: Icon, color, title, desc }) => (
              <div key={title} className="flex items-start gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                  <Icon size={18} />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{title}</p>
                  <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>

        {/* How it works */}
        <CollapsibleSection title="How it works?" subtitle="4 simple steps to get started">
          <div className="space-y-3">
            {[
              { step: "1", title: "Sign Up Free", desc: "Create your account with email and your basic details in 60 seconds." },
              { step: "2", title: "Verify Your ID", desc: "Upload your college student ID. Admin approves within 24 hours." },
              { step: "3", title: "Buy, Sell or Swap", desc: "Browse listings or post your own. Contact sellers directly on WhatsApp." },
              { step: "4", title: "Meet & Deal Safely", desc: "Meet on campus in a safe place. No money to us — direct deal between students." },
            ].map(({ step, title, desc }) => (
              <div key={step} className="flex gap-4 items-start">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">{step}</div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{title}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>

        {/* Ambassador CTA */}
        <div className="mt-8 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <TrendingUp size={20} />
            </div>
            <div>
              <p className="font-bold text-sm">Campus Ambassador Program</p>
              <p className="text-blue-200 text-xs">Represent CampusSwap at your college</p>
            </div>
          </div>
          <p className="text-blue-100 text-xs mb-4">Get verified badge, priority listing placement, and build your campus network.</p>
          <Link to="/ambassador" className="block text-center bg-white text-blue-700 font-bold py-2.5 rounded-xl text-sm hover:bg-blue-50">
            Apply as Ambassador →
          </Link>
        </div>

        {/* Top Ambassadors */}
        {topAmbassadors.length > 0 && (
          <div className="mt-8">
            <h2 className="font-bold text-gray-800 text-xl mb-1">🏆 Top Ambassadors</h2>
            <p className="text-gray-500 text-sm mb-4">Students spreading the word on their campus</p>
            <div className="space-y-2">
              {topAmbassadors.map((u, i) => (
                <div key={u.id} className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                    i === 0 ? "bg-yellow-400 text-yellow-900" : i === 1 ? "bg-gray-300 text-gray-700" : i === 2 ? "bg-amber-600 text-white" : "bg-blue-100 text-blue-700"
                  }`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 text-sm line-clamp-1">{u.name || "Student"}</p>
                    <p className="text-xs text-gray-500 line-clamp-1">{u.college || "—"}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-blue-600">{u.referralCount}</p>
                    <p className="text-xs text-gray-400">referrals</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FAQs */}
        <CollapsibleSection title="Common Questions" subtitle="Answers to what students ask most">
          <div className="space-y-2">
            {FAQS.map((faq) => <FAQItem key={faq.q} {...faq} />)}
          </div>
        </CollapsibleSection>

        {/* Final CTA */}
        <div className="mt-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-center text-white">
          <p className="font-bold text-xl mb-1">Ready to save money?</p>
          <p className="text-blue-200 text-sm mb-5">Join {stats.users > 0 ? `${fmt(stats.users)} students` : "thousands of students"} already using CampusSwap</p>
          <Link to="/signup" className="block bg-yellow-400 text-gray-900 font-bold py-3 rounded-xl text-base hover:bg-yellow-300">
            Join CampusSwap — Free
          </Link>
          <Link to="/login" className="block mt-3 text-blue-200 text-sm hover:text-white">
            Already a member? Sign In
          </Link>
        </div>

        {/* Contact & Social */}
        <CollapsibleSection title="Get in Touch" subtitle="Email, Instagram or WhatsApp">
          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
            <a href="mailto:campusswap.official@gmail.com"
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors">
              <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail size={18} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Email Support</p>
                <p className="text-xs text-gray-500">campusswap.official@gmail.com</p>
              </div>
            </a>
            <a href="https://instagram.com/campusswap_in" target="_blank" rel="noreferrer"
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-pink-50 transition-colors">
              <div className="w-9 h-9 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-lg">
                📸
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Instagram</p>
                <p className="text-xs text-gray-500">@campusswap_in</p>
              </div>
            </a>
            <a href="https://wa.me/918999325996" target="_blank" rel="noreferrer"
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-green-50 transition-colors">
              <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle size={18} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">WhatsApp Support</p>
                <p className="text-xs text-gray-500">Chat with us for quick help</p>
              </div>
            </a>
          </div>
        </CollapsibleSection>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400 mb-2">CampusSwap &copy; 2026. Made for students, by students.</p>
          <div className="flex justify-center gap-4">
            <Link to="/privacy-policy" className="text-xs text-blue-500 hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-xs text-blue-500 hover:underline">Terms &amp; Conditions</Link>
            <Link to="/contact" className="text-xs text-blue-500 hover:underline">Contact Us</Link>
          </div>
        </div>

      </div>
    </div>
  );
}
